import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import {
  MessageCircle, ShieldCheck, Cable, HardDrive, Volume2, ZoomIn,
  CheckCircle2, Tag, Gauge, Sparkles, Satellite, Gift, Cpu, Server, GraduationCap, Clock, Award, ShoppingCart,
} from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { Countdown } from "./Countdown";
import { ShareButton } from "./ShareButton";
import { PRODUCTS, PRODUCT_FILTERS, CATEGORIES, WIFI_PACKAGES, PARABOLA_PACKAGES, KOMPUTER_PACKAGES, KOMPUTER_BUILDS, NETWORK_SERVICES, POS_PACKAGES, POS_BROCHURE, KURSUS_PROGRAMS, KURSUS_FACILITIES, KURSUS_BROCHURE } from "../../lib/products";
import { waLink } from "../../lib/data";

const parseRp = (s) => parseInt(String(s).replace(/[^0-9]/g, ""), 10) || 0;
const formatRp = (n) => "Rp " + n.toLocaleString("id-ID");

const CctvCard = ({ p, i, onOpen }) => {
  const title = `${p.channel} Channel ${p.cameras} Kamera`;
  const savings = parseRp(p.priceOld) - parseRp(p.price);
  const wa = `Halo SATCOM, saya tertarik dengan Paket CCTV ${title} (${p.mp} ${p.type}) seharga ${p.price}. Mohon info lebih lanjut.`;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
      data-testid={`product-card-${p.id}`}
      className="group flex flex-col rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
    >
      <button
        onClick={onOpen}
        className="relative aspect-[3/4] overflow-hidden bg-[#F5F7FA]"
        data-testid={`product-image-${p.id}`}
        aria-label={`Lihat brosur ${title}`}
      >
        <img
          src={p.img}
          alt={`Paket CCTV Dahua ${title} ${p.mp} ${p.type} — SATCOM Rangkasbitung`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute right-3 top-3 grid place-items-center h-9 w-9 rounded-full bg-white/85 backdrop-blur text-[#0B63F6] opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn className="h-4 w-4" />
        </span>
        {savings > 0 && (
          <span
            data-testid={`product-savings-${p.id}`}
            className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-[#25D366] text-white text-xs font-bold px-3 py-1.5 shadow-lg shadow-[#25D366]/30"
          >
            <Tag className="h-3.5 w-3.5" /> Hemat {formatRp(savings)}
          </span>
        )}
      </button>

      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="rounded-full bg-[#0B63F6]/10 text-[#0B63F6] text-xs font-semibold px-2.5 py-1">{p.mp}</span>
          <span className="rounded-full bg-[#16C7E8]/15 text-[#0891a8] text-xs font-semibold px-2.5 py-1">{p.type}</span>
        </div>
        <h3 className="mt-3 font-heading font-bold text-lg text-[#111827]">{title}</h3>
        <ul className="mt-3 space-y-1.5 text-xs text-[#6B7280]">
          <li className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-[#0B63F6]" /> Garansi resmi 1 tahun</li>
          <li className="flex items-center gap-2"><Volume2 className="h-3.5 w-3.5 text-[#0B63F6]" /> Rekam suara + video</li>
          <li className="flex items-center gap-2"><HardDrive className="h-3.5 w-3.5 text-[#0B63F6]" /> Termasuk DVR + hardisk</li>
          <li className="flex items-center gap-2"><Cable className="h-3.5 w-3.5 text-[#0B63F6]" /> Kabel + pemasangan</li>
        </ul>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#9CA3AF] line-through">{p.priceOld}</span>
            {savings > 0 && (
              <span className="rounded-full bg-[#25D366]/15 text-[#1a9f4b] text-[11px] font-bold px-2 py-0.5">
                Hemat {formatRp(savings)}
              </span>
            )}
          </div>
          <span className="block font-heading font-extrabold text-2xl text-[#071A33]">{p.price}</span>
        </div>
        <a
          href={waLink(wa)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`product-wa-${p.id}`}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-5 py-3 transition-colors"
        >
          <MessageCircle className="h-4 w-4" /> Pesan via WhatsApp
        </a>
        <ShareButton testid={`product-share-${p.id}`} title={title} text={`Paket CCTV ${title} — ${p.mp} ${p.type} • ${p.price}`} />
      </div>
    </motion.div>
  );
};

const WifiCard = ({ p, i, onOpen }) => {
  const savings = parseRp(p.priceOld) - parseRp(p.price);
  const wa = `Halo SATCOM, saya tertarik dengan WiFi ${p.name} (${p.price}${p.period}). Mohon info pemasangan internet di lokasi saya.`;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.4, delay: i * 0.06 }}
      data-testid={`product-card-${p.id}`}
      className={`group relative flex flex-col rounded-2xl bg-white shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden ${
        p.popular ? "border-2 border-[#0B63F6]" : "border border-gray-100"
      }`}
    >
      {p.popular && (
        <span className="absolute right-4 top-4 z-10 inline-flex items-center gap-1 rounded-full bg-[#0B63F6] text-white text-xs font-bold px-3 py-1.5 shadow-lg">
          <Sparkles className="h-3.5 w-3.5" /> Paling Populer
        </span>
      )}
      <button
        onClick={onOpen}
        className="relative aspect-[4/3] overflow-hidden bg-[#F5F7FA]"
        data-testid={`product-image-${p.id}`}
        aria-label={`Lihat brosur ${p.name}`}
      >
        <img
          src={p.img}
          alt={`WiFi ${p.name} SATCOM ${p.speed} — internet fiber Rangkasbitung`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute right-3 bottom-3 grid place-items-center h-9 w-9 rounded-full bg-white/85 backdrop-blur text-[#0B63F6] opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn className="h-4 w-4" />
        </span>
        {savings > 0 && (
          <span
            data-testid={`product-savings-${p.id}`}
            className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-[#25D366] text-white text-xs font-bold px-3 py-1.5 shadow-lg shadow-[#25D366]/30"
          >
            <Tag className="h-3.5 w-3.5" /> Hemat {formatRp(savings)}
          </span>
        )}
      </button>

      <div className="flex flex-col flex-1 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-xl text-[#111827]">{p.name}</h3>
          <span className="inline-flex items-center gap-1 rounded-full bg-[#0B63F6]/10 text-[#0B63F6] text-xs font-bold px-3 py-1.5">
            <Gauge className="h-3.5 w-3.5" /> {p.speed}
          </span>
        </div>
        <ul className="mt-4 space-y-2 text-sm text-[#4B5563] flex-1">
          {p.features.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#16C7E8] mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#9CA3AF] line-through">{p.priceOld}{p.period}</span>
            {savings > 0 && (
              <span className="rounded-full bg-[#25D366]/15 text-[#1a9f4b] text-[11px] font-bold px-2 py-0.5">
                Hemat {formatRp(savings)}
              </span>
            )}
          </div>
          <div className="flex items-end gap-1">
            <span className="font-heading font-extrabold text-3xl text-[#071A33]">{p.price}</span>
            <span className="text-sm text-[#6B7280] mb-1">{p.period}</span>
          </div>
        </div>
        <a
          href={waLink(wa)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`product-wa-${p.id}`}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-5 py-3 transition-colors"
        >
          <MessageCircle className="h-4 w-4" /> Pasang Sekarang
        </a>
        <ShareButton testid={`product-share-${p.id}`} title={p.name} text={`WiFi ${p.name} ${p.speed} — ${p.price}${p.period}`} />
      </div>
    </motion.div>
  );
};

const BuildCard = ({ p, i }) => {
  const wa = `Halo SATCOM, saya tertarik dengan ${p.name} (${p.priceFrom}). Mohon info spek & harga sesuai budget saya.`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: i * 0.06 }}
      data-testid={`product-card-${p.id}`}
      className={`flex flex-col rounded-2xl bg-white p-6 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ${
        p.popular ? "border-2 border-[#0B63F6]" : "border border-gray-100"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br from-[#0B63F6] to-[#16C7E8] text-white">
          <p.icon className="h-5 w-5" />
        </span>
        {p.popular && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#0B63F6] text-white text-xs font-bold px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Populer
          </span>
        )}
      </div>
      <h4 className="mt-4 font-heading font-bold text-lg text-[#111827]">{p.name}</h4>
      <p className="text-sm text-[#6B7280]">{p.for}</p>
      <ul className="mt-4 space-y-2 text-sm text-[#4B5563] flex-1">
        {p.specs.map((s) => (
          <li key={s} className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-[#16C7E8] mt-0.5 shrink-0" />
            <span>{s}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 pt-4 border-t border-gray-100">
        <span className="font-heading font-extrabold text-xl text-[#071A33]">{p.priceFrom}</span>
      </div>
      <a
        href={waLink(wa)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`product-wa-${p.id}`}
        className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-5 py-3 transition-colors"
      >
        <MessageCircle className="h-4 w-4" /> Konsultasi Spek
      </a>
      <ShareButton testid={`product-share-${p.id}`} title={p.name} text={`${p.name} — ${p.priceFrom}`} />
    </motion.div>
  );
};

const NetworkCard = ({ p, i, onOpen }) => {
  const wa = `Halo SATCOM, saya tertarik dengan ${p.title} (${p.price}). Mohon info lebih lanjut.`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
      data-testid={`product-card-${p.id}`}
      className="group flex flex-col rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
    >
      <button
        onClick={onOpen}
        className="relative aspect-[3/4] overflow-hidden bg-[#F5F7FA]"
        data-testid={`product-image-${p.id}`}
        aria-label={`Lihat brosur ${p.title}`}
      >
        <img
          src={p.img}
          alt={`${p.title} — ${p.tag} SATCOM Rangkasbitung`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute right-3 top-3 grid place-items-center h-9 w-9 rounded-full bg-white/85 backdrop-blur text-[#0B63F6] opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn className="h-4 w-4" />
        </span>
      </button>
      <div className="flex flex-col flex-1 p-6">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#0B63F6]/10 text-[#0B63F6] text-xs font-semibold px-3 py-1">
          <Server className="h-3.5 w-3.5" /> {p.tag}
        </span>
        <h4 className="mt-3 font-heading font-bold text-lg text-[#111827]">{p.title}</h4>
        <ul className="mt-3 space-y-1.5 text-sm text-[#4B5563] flex-1">
          {p.features.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#16C7E8] mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 pt-4 border-t border-gray-100">
          <span className="block font-heading font-extrabold text-xl text-[#071A33]">{p.price}</span>
          <span className="block text-xs text-[#6B7280] mt-0.5">{p.priceNote}</span>
          <span className="block text-[11px] text-[#9CA3AF] mt-1">*{p.note}</span>
        </div>
        <a
          href={waLink(wa)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`product-wa-${p.id}`}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-5 py-3 transition-colors"
        >
          <MessageCircle className="h-4 w-4" /> Konsultasi via WhatsApp
        </a>
        <ShareButton testid={`product-share-${p.id}`} title={p.title} text={`${p.title} — ${p.price}`} />
      </div>
    </motion.div>
  );
};

const KursusCard = ({ p, i }) => {
  const wa = `Halo SATCOM, saya ingin mendaftar Kursus ${p.title} (${p.price}, ${p.duration}). Mohon info jadwal & pendaftaran.`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
      data-testid={`product-card-${p.id}`}
      className={`flex flex-col rounded-2xl bg-white p-6 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ${
        p.popular ? "border-2 border-[#0B63F6]" : "border border-gray-100"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br from-[#0B63F6] to-[#16C7E8] text-white font-heading font-extrabold">
          {p.no}
        </span>
        <span className={`inline-flex items-center gap-1 rounded-full text-xs font-bold px-3 py-1.5 ${p.popular ? "bg-[#0B63F6] text-white" : "bg-[#0B63F6]/10 text-[#0B63F6]"}`}>
          {p.popular && <Sparkles className="h-3.5 w-3.5" />}{p.tag}
        </span>
      </div>
      <h4 className="mt-4 font-heading font-bold text-lg text-[#111827]">{p.title}</h4>
      <ul className="mt-3 space-y-2 text-sm text-[#4B5563] flex-1">
        {p.points.map((s) => (
          <li key={s} className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-[#16C7E8] mt-0.5 shrink-0" />
            <span>{s}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 pt-4 border-t border-gray-100">
        <span className="block font-heading font-extrabold text-2xl text-[#071A33]">{p.price}</span>
        <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-[#6B7280]">
          <Clock className="h-3.5 w-3.5 text-[#0B63F6]" /> {p.duration}
        </span>
      </div>
      <a
        href={waLink(wa)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`product-wa-${p.id}`}
        className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-5 py-3 transition-colors"
      >
        <MessageCircle className="h-4 w-4" /> Daftar via WhatsApp
      </a>
      <ShareButton testid={`product-share-${p.id}`} title={p.title} text={`Kursus ${p.title} — ${p.price} (${p.duration})`} />
    </motion.div>
  );
};

const PosCard = ({ p, i }) => {
  const wa = `Halo SATCOM, saya tertarik dengan ${p.name} (${p.price}). Mohon info lebih lanjut mengenai sistem kasir.`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
      data-testid={`product-card-${p.id}`}
      className={`flex flex-col rounded-2xl bg-white p-6 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ${
        p.popular ? "border-2 border-[#0B63F6]" : "border border-gray-100"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br from-[#0B63F6] to-[#16C7E8] text-white">
          <ShoppingCart className="h-5 w-5" />
        </span>
        {p.popular && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#0B63F6] text-white text-xs font-bold px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Populer
          </span>
        )}
      </div>
      <h4 className="mt-4 font-heading font-bold text-lg text-[#111827]">{p.name}</h4>
      <p className="text-sm text-[#6B7280]">{p.for}</p>
      <ul className="mt-4 space-y-2 text-sm text-[#4B5563] flex-1">
        {p.features.map((s) => (
          <li key={s} className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-[#16C7E8] mt-0.5 shrink-0" />
            <span>{s}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 pt-4 border-t border-gray-100">
        <span className="block font-heading font-extrabold text-xl text-[#071A33]">{p.price}</span>
      </div>
      <a
        href={waLink(wa)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`product-wa-${p.id}`}
        className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-5 py-3 transition-colors"
      >
        <MessageCircle className="h-4 w-4" /> Konsultasi via WhatsApp
      </a>
      <ShareButton testid={`product-share-${p.id}`} title={p.name} text={`${p.name} — ${p.price}`} />
    </motion.div>
  );
};

export const Products = () => {
  const [category, setCategory] = useState("cctv");
  const [filter, setFilter] = useState("all");
  const [lb, setLb] = useState({ open: false, index: 0, slides: [] });

  const cctvList = useMemo(
    () => (filter === "all" ? PRODUCTS : PRODUCTS.filter((p) => String(p.channel) === filter)),
    [filter]
  );

  const cctvSlides = PRODUCTS.map((p) => ({ src: p.img, description: `${p.channel} Channel ${p.cameras} Kamera — ${p.mp} ${p.type}` }));
  const wifiSlides = WIFI_PACKAGES.map((p) => ({ src: p.img, description: `WiFi ${p.name} — ${p.speed}` }));
  const parabolaSlides = PARABOLA_PACKAGES.map((p) => ({ src: p.img, description: p.name }));
  const komputerSlides = KOMPUTER_PACKAGES.map((p) => ({ src: p.img, description: p.name }));
  const networkSlides = NETWORK_SERVICES.map((p) => ({ src: p.img, description: p.title }));
  const kursusSlides = [{ src: KURSUS_BROCHURE, description: "Kursus Privat Komputer — SATCOM" }];
  const posSlides = [{ src: POS_BROCHURE, description: "SATCOM POS System" }];

  const openLb = (slides, index) => setLb({ open: true, index, slides });

  return (
    <section id="produk" className="py-24 md:py-32 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-2xl">
          <Reveal><Overline>Produk & Harga</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-heading font-bold text-[#111827] text-4xl md:text-5xl tracking-tight">
              Produk & Paket SATCOM
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-[#6B7280] text-base md:text-lg leading-relaxed">
              Pilih kategori produk. Harga dapat menyesuaikan kondisi lokasi —
              konsultasikan dulu via WhatsApp untuk rekomendasi terbaik.
            </p>
          </Reveal>
        </div>

        {/* Category tabs */}
        <Reveal delay={0.12}>
          <div className="mt-9 inline-flex flex-wrap gap-2 rounded-full bg-white border border-gray-200 p-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                data-testid={`product-category-${c.key}`}
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all ${
                  category === c.key
                    ? "bg-[#071A33] text-white shadow-md"
                    : "text-[#6B7280] hover:text-[#071A33]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        {["wifi", "parabola", "komputer", "pos"].includes(category) && <Countdown />}

        {/* CCTV */}
        {category === "cctv" && (
          <>
            <div className="mt-6 flex flex-wrap gap-3">
              {PRODUCT_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`product-filter-${f.key}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                    filter === f.key
                      ? "bg-gradient-to-r from-[#0B63F6] to-[#16C7E8] text-white shadow-lg shadow-[#0B63F6]/25"
                      : "bg-white text-[#111827] border border-gray-200 hover:border-[#0B63F6]/40"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <motion.div layout className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {cctvList.map((p, i) => (
                  <CctvCard key={p.id} p={p} i={i} onOpen={() => openLb(cctvSlides, PRODUCTS.findIndex((x) => x.id === p.id))} />
                ))}
              </AnimatePresence>
            </motion.div>
          </>
        )}

        {/* WiFi */}
        {category === "wifi" && (
          <>
            <Reveal delay={0.05}>
              <p className="mt-6 text-sm text-[#6B7280]">
                Internet Full Fiber tanpa FUP & unlimited perangkat. Promo terbatas — harga per bulan.
              </p>
            </Reveal>
            <motion.div layout className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              <AnimatePresence mode="popLayout">
                {WIFI_PACKAGES.map((p, i) => (
                  <WifiCard key={p.id} p={p} i={i} onOpen={() => openLb(wifiSlides, i)} />
                ))}
              </AnimatePresence>
            </motion.div>
          </>
        )}

        {/* Parabola */}
        {category === "parabola" && (
          <div className="mt-6 space-y-6">
            {PARABOLA_PACKAGES.map((p) => {
              const savings = parseRp(p.priceOld) - parseRp(p.price);
              const wa = `Halo SATCOM, saya tertarik dengan Paket ${p.name} (${p.price} NET). Mohon info pemasangan parabola.`;
              return (
                <Reveal key={p.id}>
                  <div
                    data-testid={`product-card-${p.id}`}
                    className="grid lg:grid-cols-2 gap-0 rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden"
                  >
                    <button
                      onClick={() => openLb(parabolaSlides, 0)}
                      className="group relative aspect-square lg:aspect-auto overflow-hidden bg-[#F5F7FA]"
                      data-testid={`product-image-${p.id}`}
                      aria-label={`Lihat brosur ${p.name}`}
                    >
                      <img
                        src={p.img}
                        alt={`${p.name} — parabola K-Vision Nex Parabola Transvision SATCOM Rangkasbitung`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute right-3 top-3 grid place-items-center h-9 w-9 rounded-full bg-white/85 backdrop-blur text-[#0B63F6] opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="h-4 w-4" />
                      </span>
                      {savings > 0 && (
                        <span
                          data-testid={`product-savings-${p.id}`}
                          className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-[#25D366] text-white text-xs font-bold px-3 py-1.5 shadow-lg shadow-[#25D366]/30"
                        >
                          <Tag className="h-3.5 w-3.5" /> Hemat {formatRp(savings)}
                        </span>
                      )}
                    </button>

                    <div className="flex flex-col p-7 md:p-10">
                      <div className="flex items-center gap-2">
                        <span className="grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br from-[#0B63F6] to-[#16C7E8] text-white">
                          <Satellite className="h-5 w-5" />
                        </span>
                        <h3 className="font-heading font-bold text-2xl text-[#111827]">{p.name}</h3>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {p.brands.map((b) => (
                          <span key={b} className="rounded-full bg-[#0B63F6]/10 text-[#0B63F6] text-xs font-semibold px-3 py-1">{b}</span>
                        ))}
                      </div>

                      <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#16C7E8]/10 text-[#0891a8] text-sm font-semibold px-4 py-2.5 w-fit">
                        <Gift className="h-4 w-4" /> Gratis paket premium 1 tahun
                      </div>

                      <ul className="mt-5 space-y-2 text-sm text-[#4B5563]">
                        {p.features.map((f) => (
                          <li key={f} className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-[#16C7E8] mt-0.5 shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 pt-5 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#9CA3AF] line-through">{p.priceOld}</span>
                          {savings > 0 && (
                            <span className="rounded-full bg-[#25D366]/15 text-[#1a9f4b] text-[11px] font-bold px-2 py-0.5">
                              Hemat {formatRp(savings)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-end gap-2">
                          <span className="font-heading font-extrabold text-4xl text-[#071A33]">{p.price}</span>
                          <span className="text-sm text-[#6B7280] mb-1.5 font-semibold">NET</span>
                        </div>
                        <p className="mt-1 text-xs text-[#6B7280]">{p.note}</p>
                      </div>

                      <a
                        href={waLink(wa)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-wa-${p.id}`}
                        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-6 py-3.5 transition-colors w-fit"
                      >
                        <MessageCircle className="h-5 w-5" /> Pesan via WhatsApp
                      </a>
                      <ShareButton testid={`product-share-${p.id}`} title={p.name} text={`${p.name} — ${p.price} NET`} />
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}

        {/* Komputer & Laptop */}
        {category === "komputer" && (
          <div className="mt-6 space-y-6">
            {KOMPUTER_PACKAGES.map((p) => {
              const wa = `Halo SATCOM, saya ingin konsultasi ${p.name}. Mohon info spesifikasi & harga sesuai budget saya.`;
              return (
                <Reveal key={p.id}>
                  <div
                    data-testid={`product-card-${p.id}`}
                    className="grid lg:grid-cols-2 gap-0 rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden"
                  >
                    <button
                      onClick={() => openLb(komputerSlides, 0)}
                      className="group relative aspect-square lg:aspect-auto overflow-hidden bg-[#F5F7FA]"
                      data-testid={`product-image-${p.id}`}
                      aria-label={`Lihat brosur ${p.name}`}
                    >
                      <img
                        src={p.img}
                        alt={`${p.name} — rakitan PC gaming & laptop ASUS Acer Dell HP di SATCOM Rangkasbitung`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute right-3 top-3 grid place-items-center h-9 w-9 rounded-full bg-white/85 backdrop-blur text-[#0B63F6] opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="h-4 w-4" />
                      </span>
                      <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-[#0B63F6] text-white text-xs font-bold px-3 py-1.5 shadow-lg">
                        <Sparkles className="h-3.5 w-3.5" /> Budget Anda, Spek Anda
                      </span>
                    </button>

                    <div className="flex flex-col p-7 md:p-10">
                      <div className="flex items-center gap-2">
                        <span className="grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br from-[#0B63F6] to-[#16C7E8] text-white">
                          <Cpu className="h-5 w-5" />
                        </span>
                        <h3 className="font-heading font-bold text-2xl text-[#111827]">{p.name}</h3>
                      </div>

                      <p className="mt-3 text-[#6B7280]">{p.tagline}</p>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {p.brands.map((b) => (
                          <span key={b} className="rounded-full bg-[#0B63F6]/10 text-[#0B63F6] text-xs font-semibold px-3 py-1">{b}</span>
                        ))}
                      </div>

                      <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#16C7E8]/10 text-[#0891a8] text-sm font-semibold px-4 py-2.5 w-fit">
                        <Gift className="h-4 w-4" /> Gratis software utama (OS, driver & game)
                      </div>

                      <ul className="mt-5 grid sm:grid-cols-2 gap-x-5 gap-y-2 text-sm text-[#4B5563]">
                        {p.features.map((f) => (
                          <li key={f} className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-[#16C7E8] mt-0.5 shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 pt-5 border-t border-gray-100">
                        <span className="font-heading font-extrabold text-4xl text-[#071A33]">{p.price}</span>
                        <p className="mt-1 text-xs text-[#6B7280]">{p.note}</p>
                      </div>

                      <a
                        href={waLink(wa)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-wa-${p.id}`}
                        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-6 py-3.5 transition-colors w-fit"
                      >
                        <MessageCircle className="h-5 w-5" /> Konsultasi Spek & Harga
                      </a>
                      <ShareButton testid={`product-share-${p.id}`} title={p.name} text={`${p.name} — ${p.price}`} />
                    </div>
                  </div>
                </Reveal>
              );
            })}

            <div>
              <h3 className="font-heading font-bold text-xl text-[#111827]">Contoh Paket Rakitan PC</h3>
              <p className="mt-1 text-sm text-[#6B7280]">Harga di bawah bersifat estimasi — spek & komponen dapat disesuaikan dengan budget Anda.</p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                {KOMPUTER_BUILDS.map((b, i) => <BuildCard key={b.id} p={b} i={i} />)}
              </div>
            </div>
          </div>
        )}

        {/* Network */}
        {category === "network" && (
          <>
            <Reveal delay={0.05}>
              <p className="mt-6 text-sm text-[#6B7280]">Jasa instalasi & konfigurasi server untuk bisnis, kantor, dan warnet — MikroTik, Windows Server, dan Linux Server.</p>
            </Reveal>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {NETWORK_SERVICES.map((s, i) => (
                <NetworkCard key={s.id} p={s} i={i} onOpen={() => openLb(networkSlides, i)} />
              ))}
            </div>
          </>
        )}

        {/* POS Kasir */}
        {category === "pos" && (
          <>
            <Reveal delay={0.05}>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-[#6B7280]">Sistem kasir modern & terpercaya (hardware + software) untuk toko retail, minimarket, cafe, dan restoran.</p>
                <button
                  onClick={() => openLb(posSlides, 0)}
                  data-testid="pos-brochure-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 hover:border-[#0B63F6]/40 text-[#111827] text-sm font-semibold px-5 py-2.5 transition-colors"
                >
                  <ZoomIn className="h-4 w-4 text-[#0B63F6]" /> Lihat Brosur POS
                </button>
              </div>
            </Reveal>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              {POS_PACKAGES.map((s, i) => <PosCard key={s.id} p={s} i={i} />)}
            </div>
          </>
        )}

        {/* Kursus */}
        {category === "kursus" && (
          <>
            <Reveal delay={0.05}>
              <div className="mt-6 rounded-2xl bg-[#071A33] p-6 md:p-8">
                <div className="flex items-center gap-2 text-white">
                  <GraduationCap className="h-5 w-5 text-[#16C7E8]" />
                  <h3 className="font-heading font-bold text-lg">Kursus Privat Komputer — 1-on-1 Intensif</h3>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                  {KURSUS_FACILITIES.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm text-white/80">
                      <Award className="h-4 w-4 text-[#16C7E8] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => openLb(kursusSlides, 0)}
                  data-testid="kursus-brochure-btn"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                >
                  <ZoomIn className="h-4 w-4" /> Lihat Brosur Kursus
                </button>
              </div>
            </Reveal>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {KURSUS_PROGRAMS.map((s, i) => <KursusCard key={s.id} p={s} i={i} />)}
            </div>
          </>
        )}

        <Reveal delay={0.1}>
          <div className="mt-10 flex items-start gap-2 text-sm text-[#6B7280]">
            <CheckCircle2 className="h-4 w-4 text-[#0B63F6] mt-0.5 shrink-0" />
            <p>Butuh spesifikasi berbeda atau paket khusus? Kami bisa sesuaikan dengan kebutuhan & kondisi lokasi Anda.</p>
          </div>
        </Reveal>
      </div>

      <Lightbox
        open={lb.open}
        close={() => setLb({ ...lb, open: false })}
        index={lb.index}
        slides={lb.slides}
      />
    </section>
  );
};
